/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable unused-imports/no-unused-vars */
import { FormControl, FormControlLabel, Grid, MenuItem, Slider, Switch, styled } from "@mui/material";
import ColorPickerInput from '@/components/color-picker-input/color-picker-input';
import Select from '@/components/select/select';
import Typography from '@/components/typography/typography';
import formatText from '@/utils/format-text';
import { useEffect } from "react";

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 16,
      height: 16,
    },
    '&::before': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    '&::after': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 16,
    height: 16,
    margin: 2,
    // color: theme.palette.theme.primary[800]
  },
  '& .Mui-checked': {
    color: `${theme.palette.theme.primary[800]} !important`,
  },
  '& .Mui-checked+.MuiSwitch-track': {
    backgroundColor: `${theme.palette.theme.primary[800]} !important`,
  },
}));

export interface BorderControlProps {
  styles: Record<string, any>,
  onChange: (key: string, value: any) => void
  isBordered: boolean;
  disableWidth?: boolean;
  disableStyle?: boolean;
  disableColor?: boolean;
  disableCollapse?: boolean;
  disableRadius?: boolean;
  initiateStyles?: (styles: Record<string, any>) => void;
}

const borderStyles = [
  'none',
  'solid',
  'dashed',
  'dotted',
  'double',
  'groove',
  'ridge',
  'inset',
  'outset'
];

const BorderControl: React.FC<BorderControlProps> = ({
  styles,
  onChange,
  isBordered,
  disableWidth = false,
  disableStyle = false,
  disableColor = false,
  disableCollapse = false,
  disableRadius = false,
  initiateStyles
}) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Android12Switch
              checked={isBordered}
              onChange={(e) => {
                const checked = e.target.checked;
                if (initiateStyles) {
                  if (!checked) {
                    initiateStyles({
                      is_bordered: false,
                      border: 0,
                      borderStyle: 'none',
                      borderColor: '#000000',
                      borderCollapse: 'separate',
                      borderRadius: 0,
                    });
                  } else {
                    initiateStyles({
                      is_bordered: true,
                      border: 1,
                      borderStyle: 'solid',
                      borderColor: '#000000',
                      borderCollapse: 'collapse',
                      borderRadius: 0,
                    });
                  }
                } else {
                  onChange('is_bordered', checked);
                }
              }}
            />
          }
          label={<Typography type='s5' weight='medium' color='theme.secondary.800'>Border</Typography>}
          labelPlacement="start"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: 'auto' }}
        />
      </Grid>

      {isBordered && (
        <>
          {/* Width */}
          {!disableWidth && (
            <Grid item xs={6}>
              <Select
                value={styles.border || 1}
                onChange={(e) => onChange('border', e.target.value)}
                label="Border Width"
                fullWidth
                size="small"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((wd) => (
                  <MenuItem key={wd} value={wd}>
                    <Typography type='s5' color='theme.secondary.1000'>{wd}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}

          {/* Style */}
          {!disableStyle && (
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Select
                  value={styles.borderStyle || 'solid'}
                  onChange={(e) => onChange('borderStyle', e.target.value)}
                  renderValue={(value) => formatText(value)}
                  label="Border Style"
                >
                  {borderStyles.map((style) => (
                    <MenuItem key={style} value={style}>
                      <Typography type='s5' color='theme.secondary.1000'>{formatText(style)}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Color */}
          {!disableColor && (
            <Grid item xs={6}>
              <Typography type='s5' weight='medium' color='theme.secondary.800' mb={0.5}>Border Color</Typography>
              <ColorPickerInput
                value={styles.borderColor || '#e0e0e0'}
                onChange={(color) => onChange('borderColor', color)}
                fullWidth
              />
            </Grid>
          )}

          {/* Collapse */}
          {!disableCollapse && (
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <Select
                  value={styles.borderCollapse || 'collapse'}
                  onChange={(e) => onChange('borderCollapse', e.target.value)}
                  label="Border Collapse"
                  renderValue={(value) => formatText(value)}
                >
                  <MenuItem value="collapse"><Typography type='s5' color='theme.secondary.1000'>Collapse</Typography></MenuItem>
                  <MenuItem value="separate"><Typography type='s5' color='theme.secondary.1000'>Separate</Typography></MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Radius */}
          {!disableRadius && (
            <Grid item xs={12}>
              <Typography type='s5' weight='medium' color='theme.secondary.800' variant="caption">Border Radius</Typography>
              <Slider
                value={typeof styles.borderRadius === 'number' ? styles.borderRadius : 1}
                min={0}
                max={20}
                step={1}
                size="small"
                onChange={(_, value) => onChange('borderRadius', value)}
                valueLabelDisplay="auto"
                sx={(theme) => ({
                  '& .MuiSlider-rail': {
                    color: theme.palette.theme.primary[500],
                  },
                  '& .MuiSlider-thumb': {
                    color: theme.palette.theme.primary[800],
                  },
                  '& .MuiSlider-track': {
                    color: theme.palette.theme.primary[600],
                  },
                })}
              />
            </Grid>
          )}
        </>
      )}

    </Grid>
  );
}

export default BorderControl;